import { EditorState } from "@codemirror/state";
import { EditorView, highlightActiveLine, lineNumbers, keymap } from "@codemirror/view";
import { json } from "@codemirror/lang-json";
import { xml } from "@codemirror/lang-xml";
import { html } from "@codemirror/lang-html";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { syntaxHighlighting, defaultHighlightStyle } from "@codemirror/language";
import type { Language } from "./types";

export function createTextFormatterEditor(options: {
    parent: HTMLElement;
    doc?: string;
    language: Language;
    readOnly?: boolean;
    onChange?: (content: string) => void;
}): EditorView {
    const extensions = [
        // Basic display features
        lineNumbers(),
        highlightActiveLine(),
        EditorView.lineWrapping,

        // Enable syntax highlighting with default color scheme
        syntaxHighlighting(defaultHighlightStyle),

        // Enable history (undo/redo) functionality
        history(),

        // Keymaps for editing, including history commands
        keymap.of([...historyKeymap, ...defaultKeymap]),

        // Global error handler for parser issues
        EditorView.exceptionSink.of((exception) => {
            console.error("CodeMirror exception caught by sink:", exception);
        }),
    ];

    // Add language support
    const languageExtension = getLanguageExtension(options.language);
    if (languageExtension) {
        extensions.push(languageExtension);
    }

    // Make read-only if specified
    if (options.readOnly) {
        extensions.push(EditorState.readOnly.of(true));
        extensions.push(
            EditorView.editorAttributes.of({
                class: "cm-readonly",
            })
        );
    }

    // Add change listener if specified
    if (options.onChange && !options.readOnly) {
        extensions.push(
            EditorView.updateListener.of((update) => {
                if (update.docChanged) {
                    const content = update.state.doc.toString();
                    options.onChange?.(content);
                }
            })
        );
    }

    // Create editor state
    const state = EditorState.create({
        doc: options.doc || "",
        extensions,
    });

    // Create and return the editor view
    return new EditorView({
        state,
        parent: options.parent,
    });
}

/**
 * Get language extension for CodeMirror
 */
function getLanguageExtension(language: Language) {
    switch (language) {
        case "json":
            return json();
        case "xml":
            return xml();
        case "html":
            return html();
        case "text":
            return null; // Plain text, no highlighting
        default:
            return null;
    }
}

/**
 * Update editor language - Note: Due to CodeMirror limitations, 
 * it's easier to recreate the editor than reconfigure language extensions
 */
export function updateEditorLanguage(editor: EditorView, language: Language): void {
    // Language switching is handled by recreating the editor
    // This function is kept for API compatibility but is not the recommended approach
    // Instead, destroy and recreate the editor when language changes
    console.warn("Language switching requires editor recreation");
}

export { EditorState, EditorView };
